import * as actionTypes from 'actions';
import { mergeUpdatedWalk } from 'utils/walkListRow';

const defaultSubjectAreaContent = {
  subjectArea: 'RIGHT_SIDE',
  pocketLocation: '',
  objectType: null,
  objectModel: null
};

const initialState = {
  eventDataSetLoading: false,
  eventsFilterIndex: 0,
  eventDataSet: {
    filters: [],
    createdBy: {}
  },

  events: {
    pageNumber: 1,
    rowCount: 0,
    loading: false,
    items: [],
    pageSize: 250,
    filter: {
      objectPresent: false,
      objectNotPresent: false,
      alertDetected: null,
      columnSorts: [],
      specialInterest: null,
      dataPointId: '',
      asWalkedMaxTorsoScoreMin: '',
      asWalkedMaxTorsoScoreMax: ''
    }
  },

  /* Walks ticked individually, or every walk matching the current view. The two are exclusive:
     the server resolves the "all matching" case itself, so no ids are held for it. */
  walkSelection: {
    selectedWalks: [],
    allMatchingSelected: false,
    updating: false
  },

  /* Set when an analysis inclusion changes, so the analysis tab refetches the next time it is
     opened rather than showing figures that predate the change */
  analysisOverviewStale: false,

  /* The annotation being composed in the annotate walks modal, and the contents it will apply. Held
     against the collection rather than the modal so the request state survives it being closed. */
  annotation: {
    content: defaultSubjectAreaContent,
    contents: [],
    processing: false
  },

  objectTypes: [],

  eventsDetailsEventS3Files: [],
  eventsDetailsEventTrainerData: {
    trainer: {
      s3Info: {}
    }
  },
  testingAnalysisOverview: {
    loading: false,
    analysisOverview: {},
    setupAnalysesCount: 0,
    eventCount: 0,
    noObjectEventCount: 0,
    locationEventCounts: {},
    subModels: []
  },
  torsoThreshold: 50,
  eventsDetailsEventEvent: null,
  eventsDetailsSession: {},
  eventsDetailsSessionLoading: false,
  commentsEvent: {},
  eventDataSetSummaryLoading: false,
  eventDataSetSummary: null,
  eventDataSetSummaryMessage: null,
  modelConfiguration: null,
  modelSet: null,
  tritonModel: null,
  analysisCell: null,
  maleBmiCategoryPercentages: [],
  maleBmiCategoryPercentagesLoading: false,
  femaleBmiCategoryPercentages: [],
  femaleBmiCategoryPercentagesLoading: false,
  maleHeightCategoryPercentages: [],
  maleHeightCategoryPercentagesLoading: false,
  femaleHeightCategoryPercentages: [],
  femaleHeightCategoryPercentagesLoading: false,
  comparisonDataSets: [],
  firstWalkEntryImage: null
};

const EVENT_LIST_PAGE = 'EVENT_DATA_SET_DETAILS_EVENT_LIST';

const eventDataSetDetailsReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.FIND_EVENT_DATA_SET_PENDING: {
      return {
        ...initialState,
        eventDataSetLoading: true
      };
    }

    case actionTypes.FIND_FIRST_SESSION_WALK_ENTRY_IMAGE_PENDING: {
      return {
        ...state,
        firstWalkEntryImage: initialState.firstWalkEntryImage
      };
    }

    case actionTypes.FIND_FIRST_SESSION_WALK_ENTRY_IMAGE_FULFILLED: {
      return {
        ...state,
        firstWalkEntryImage: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_FULFILLED: {
      return {
        ...state,
        eventDataSetLoading: false,
        eventDataSet: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_SILENT_FULFILLED: {
      return {
        ...state,
        eventDataSetLoading: false,
        eventDataSet: action.payload.data.result
      };
    }

    case actionTypes.CREATE_EVENT_DATA_SET_EVENT_DOWNLOAD_FILE_FULFILLED: {
      return {
        ...state,
        eventDataSetLoading: false,
        eventDataSet: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_SUMMARY_PENDING: {
      return {
        ...state,
        eventDataSetSummary: initialState.eventDataSetSummary,
        eventDataSetSummaryMessage: initialState.eventDataSetSummaryMessage,
        eventDataSetSummaryLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_SUMMARY_FULFILLED: {
      return {
        ...state,
        eventDataSetSummaryLoading: false,
        eventDataSetSummary: action.payload.data.success
          ? action.payload.data.result
          : null,
        eventDataSetSummaryMessage: action.payload.data.success
          ? null
          : action.payload.data.message
      };
    }

    case actionTypes.EVENT_DATA_SET_EVENTS_PAGINATED_LIST_PENDING: {
      return {
        ...state,
        events: {
          ...state.events,
          loading: true,
          loadFailed: false
        }
      };
    }

    /* Rows left from the previous load would show another model's scores */
    case actionTypes.EVENT_DATA_SET_EVENTS_PAGINATED_LIST_REJECTED: {
      return {
        ...state,
        events: {
          ...state.events,
          items: [],
          rowCount: 0,
          loading: false,
          loadFailed: true
        }
      };
    }

    case actionTypes.EVENT_DATA_SET_EVENTS_PAGINATED_LIST_FULFILLED: {
      return {
        ...state,
        events: {
          ...action.payload.data.result,
          pageSize: state.events.pageSize,
          filter: {
            ...state.events.filter,
            ...action.payload.data.result.filter
          },

          loading: false
        }
      };
    }

    case actionTypes.SELECT_EVENT_DATA_SET_DETAILS_WALK: {
      const walk = action.payload;

      /* With every matching walk selected there is no id list to subtract one row from, so a click
         degrades the selection to nothing rather than inverting into just the clicked walk */
      if (state.walkSelection.allMatchingSelected) {
        return {
          ...state,
          walkSelection: initialState.walkSelection
        };
      }

      const selected = state.walkSelection.selectedWalks.some(
        candidate => candidate.id === walk.id
      );

      return {
        ...state,
        walkSelection: {
          ...state.walkSelection,
          selectedWalks: selected
            ? state.walkSelection.selectedWalks.filter(
                candidate => candidate.id !== walk.id
              )
            : state.walkSelection.selectedWalks.concat(walk),
          allMatchingSelected: false
        }
      };
    }

    case actionTypes.SELECT_ALL_MATCHING_EVENT_DATA_SET_DETAILS_WALKS: {
      return {
        ...state,
        walkSelection: {
          ...state.walkSelection,
          selectedWalks: initialState.walkSelection.selectedWalks,
          allMatchingSelected: action.payload
        }
      };
    }

    case actionTypes.MARK_EVENT_DATA_SET_ANALYSIS_OVERVIEW_STALE: {
      return {
        ...state,
        analysisOverviewStale: true
      };
    }

    case actionTypes.CLEAR_EVENT_DATA_SET_DETAILS_WALK_SELECTION: {
      return {
        ...state,
        walkSelection: initialState.walkSelection
      };
    }

    case actionTypes.INCLUDE_WALKS_IN_ANALYSIS_PENDING:
    case actionTypes.EXCLUDE_WALKS_FROM_ANALYSIS_PENDING: {
      return {
        ...state,
        walkSelection: {
          ...state.walkSelection,
          updating: true
        }
      };
    }

    case actionTypes.INCLUDE_WALKS_IN_ANALYSIS_FULFILLED:
    case actionTypes.EXCLUDE_WALKS_FROM_ANALYSIS_FULFILLED: {
      /* The ws interceptor resolves failed requests, so a rejection arrives here as success false.
         Nothing was applied, so a selection built across pages is kept for the retry. */
      if (!action.payload?.data?.success) {
        return {
          ...state,
          walkSelection: {
            ...state.walkSelection,
            updating: false
          }
        };
      }
      return {
        ...state,
        walkSelection: initialState.walkSelection
      };
    }

    case actionTypes.INCLUDE_WALKS_IN_ANALYSIS_REJECTED:
    case actionTypes.EXCLUDE_WALKS_FROM_ANALYSIS_REJECTED: {
      return {
        ...state,
        walkSelection: {
          ...state.walkSelection,
          updating: false
        }
      };
    }

    case actionTypes.FIND_OBJECT_TYPES_FULFILLED: {
      return {
        ...state,
        objectTypes: action.payload.data.result
      };
    }

    case actionTypes.RESET_WALK_COLLECTION_ANNOTATION: {
      return {
        ...state,
        annotation: initialState.annotation
      };
    }

    case actionTypes.SET_WALK_COLLECTION_ANNOTATION_FIELD: {
      return {
        ...state,
        annotation: {
          ...state.annotation,
          content: {
            ...state.annotation.content,
            [action.payload.field]: action.payload.value
          }
        }
      };
    }

    case actionTypes.ADD_WALK_COLLECTION_ANNOTATION_CONTENT: {
      return {
        ...state,
        annotation: {
          ...state.annotation,
          content: initialState.annotation.content,
          contents: state.annotation.contents.concat(action.payload)
        }
      };
    }

    case actionTypes.REMOVE_WALK_COLLECTION_ANNOTATION_CONTENT: {
      return {
        ...state,
        annotation: {
          ...state.annotation,
          contents: state.annotation.contents.filter(
            (unused, index) => index !== action.payload
          )
        }
      };
    }

    case actionTypes.ANNOTATE_WALK_COLLECTION_WALKS_PENDING: {
      return {
        ...state,
        annotation: {
          ...state.annotation,
          processing: true
        }
      };
    }

    case actionTypes.ANNOTATE_WALK_COLLECTION_WALKS_FULFILLED: {
      /* The ws interceptor resolves failed requests, so a rejection arrives here as success false.
         Nothing was annotated, so a selection built across pages is kept for the retry. */
      if (!action.payload?.data?.success) {
        return {
          ...state,
          annotation: {
            ...state.annotation,
            processing: false
          }
        };
      }
      return {
        ...state,
        annotation: initialState.annotation,
        walkSelection: initialState.walkSelection
      };
    }

    case actionTypes.ANNOTATE_WALK_COLLECTION_WALKS_REJECTED: {
      return {
        ...state,
        annotation: {
          ...state.annotation,
          processing: false
        }
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_S3_FILES_PENDING: {
      return {
        ...state,
        eventsDetailsEventS3Files: initialState.eventsDetailsEventS3Files
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_S3_FILES_FULFILLED: {
      return {
        ...state,
        eventsDetailsEventS3Files: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_TRAINER_DATA_PENDING: {
      return {
        ...state,
        eventsDetailsEventTrainerData:
          initialState.eventsDetailsEventTrainerData
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_TRAINER_DATA_FULFILLED: {
      return {
        ...state,
        eventsDetailsEventTrainerData: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_EVENT_PENDING: {
      return {
        ...state,
        eventsDetailsEventEvent: initialState.eventsDetailsEventEvent
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_EVENT_EVENT_FULFILLED: {
      return {
        ...state,
        eventsDetailsEventEvent: action.payload.data.result
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_SESSION_PENDING: {
      return {
        ...state,
        eventsDetailsSession: initialState.eventsDetailsSession,
        eventsDetailsSessionLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_EVENTS_DETAILS_SESSION_FULFILLED: {
      return {
        ...state,
        eventsDetailsSession: action.payload.data.result,
        eventsDetailsSessionLoading: false
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_COMMENTS_EVENT: {
      return {
        ...state,
        commentsEvent: {
          ...action.payload,
          comments: !action.payload.comments ? [] : action.payload.comments
        }
      };
    }

    case actionTypes.ADD_COMMENT_FULFILLED:
    case actionTypes.SAVE_COMMENT_FULFILLED:
    case actionTypes.DELETE_COMMENT_FULFILLED: {
      const commentEvent = action.payload.data.result;
      const index = state.events.map(e => e.id).indexOf(commentEvent.id);
      const events = state.events.slice();

      if (index > -1) {
        const event = {
          ...events[index]
        };
        event.comments = !action.payload.data.result.comments
          ? []
          : action.payload.data.result.comments;
        events[index] = event;
      }

      return {
        events: events,
        ...state,
        commentsEvent: {
          ...state.commentsEvent,
          comments: !action.payload.data.result.comments
            ? []
            : action.payload.data.result.comments
        },
        detailsEvent: {
          ...state.detailsEvent,
          comments: !action.payload.data.result.comments
            ? []
            : action.payload.data.result.comments
        }
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_EVENTS_FILTER_INDEX: {
      return {
        ...state,
        eventsFilterIndex: action.payload
      };
    }

    case actionTypes.EVENT_DATA_SET_UPDATED: {
      const dataSet = action.payload.result;

      let result;
      if (dataSet.id === state.eventDataSet.id) {
        result = {
          ...state,
          eventDataSet: {
            ...state.eventDataSet,
            ...action.payload.result
          }
        };
      } else {
        result = state;
      }

      return result;
    }

    case actionTypes.FIND_EVENT_DATA_SET_ANALYSIS_OVERVIEW_PENDING: {
      /* The refetch this flag asked for is under way */
      return {
        ...state,
        analysisOverviewStale: false,
        testingAnalysisOverview: {
          ...initialState.testingAnalysisOverview,
          loading: true
        }
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_ANALYSIS_OVERVIEW_REJECTED: {
      return {
        ...state,
        testingAnalysisOverview: {
          ...initialState.testingAnalysisOverview,
          loading: false
        }
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_ANALYSIS_OVERVIEW_FULFILLED: {
      return {
        ...state,
        testingAnalysisOverview: {
          ...action.payload.data.result,
          loading: false
        }
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_ANALYSIS_TORSO_THRESHOLD: {
      const { torsoThreshold } = action.payload;

      return {
        ...state,
        torsoThreshold: torsoThreshold
      };
    }

    case actionTypes.CLEAR_FILTER_VALUES: {
      if (action.payload.pageName !== EVENT_LIST_PAGE) return state;

      return {
        ...state,
        events: {
          ...state.events,
          filter: {
            ...initialState.events.filter,
            columnSorts: state.events.filter.columnSorts.slice(),
            search: state.events.filter.search
          }
        }
      };
    }

    case actionTypes.GET_FILTER_VALUES_FULFILLED: {
      if (action.payload.data.result?.page !== EVENT_LIST_PAGE) return state;

      return {
        ...state,
        events: {
          ...state.events,
          filter: {
            ...initialState.events.filter,
            ...action.payload.data.result.filter
          }
        }
      };
    }

    case actionTypes.SET_FILTER_VALUE: {
      if (action.payload.pageName !== EVENT_LIST_PAGE) return state;

      return {
        ...state,
        events: {
          ...state.events,
          filter: {
            ...state.events.filter,
            ...action.payload.value
          }
        }
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_MODEL_CONFIGURATION: {
      return {
        ...state,
        modelConfiguration: action.payload,
        modelSet: null,
        tritonModel: null
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_MODEL_SET: {
      return {
        ...state,
        modelConfiguration: null,
        modelSet: action.payload,
        tritonModel: null
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_TRITON_MODEL: {
      return {
        ...state,
        modelConfiguration: null,
        modelSet: null,
        tritonModel: action.payload
      };
    }

    case actionTypes.SET_EVENT_DATA_SET_DETAILS_ANALYSIS_CELL: {
      return {
        ...state,
        analysisCell: action.payload
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_MALE_BMI_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          maleBmiCategoryPercentages: []
        },
        maleBmiCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_MALE_BMI_CATEGORY_PERCENTAGES_FULFILLED: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          maleBmiCategoryPercentages: action.payload.data.result
        },
        maleBmiCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_FEMALE_BMI_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          femaleBmiCategoryPercentages: []
        },
        femaleBmiCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_FEMALE_BMI_CATEGORY_PERCENTAGES_FULFILLED: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          femaleBmiCategoryPercentages: action.payload.data.result
        },
        femaleBmiCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_MALE_HEIGHT_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          maleHeightCategoryPercentages: []
        },
        maleHeightCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_MALE_HEIGHT_CATEGORY_PERCENTAGES_FULFILLED: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          maleHeightCategoryPercentages: action.payload.data.result
        },
        maleHeightCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_FEMALE_HEIGHT_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          femaleHeightCategoryPercentages: []
        },
        femaleHeightCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_EVENT_DATA_SET_FEMALE_HEIGHT_CATEGORY_PERCENTAGES_FULFILLED: {
      return {
        ...state,
        eventDataSet: {
          ...state.eventDataSet,
          femaleHeightCategoryPercentages: action.payload.data.result
        },
        femaleHeightCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_MALE_BMI_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        maleBmiCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_MALE_BMI_CATEGORY_PERCENTAGES_FULFILLED: {
      const comparisonDataSets = state.comparisonDataSets.slice();
      comparisonDataSets[comparisonDataSets.length - 1] = {
        ...state.comparisonDataSets[state.comparisonDataSets.length - 1],
        maleBmiCategoryPercentages: action.payload.data.result
      };

      return {
        ...state,
        comparisonDataSets: comparisonDataSets,
        maleBmiCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_FEMALE_BMI_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        femaleBmiCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_FEMALE_BMI_CATEGORY_PERCENTAGES_FULFILLED: {
      const comparisonDataSets = state.comparisonDataSets.slice();
      comparisonDataSets[comparisonDataSets.length - 1] = {
        ...state.comparisonDataSets[state.comparisonDataSets.length - 1],
        femaleBmiCategoryPercentages: action.payload.data.result
      };

      return {
        ...state,
        comparisonDataSets: comparisonDataSets,
        femaleBmiCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_MALE_HEIGHT_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        maleHeightCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_MALE_HEIGHT_CATEGORY_PERCENTAGES_FULFILLED: {
      const comparisonDataSets = state.comparisonDataSets.slice();
      comparisonDataSets[comparisonDataSets.length - 1] = {
        ...state.comparisonDataSets[state.comparisonDataSets.length - 1],
        maleHeightCategoryPercentages: action.payload.data.result
      };

      return {
        ...state,
        comparisonDataSets: comparisonDataSets,
        maleHeightCategoryPercentagesLoading: false
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_FEMALE_HEIGHT_CATEGORY_PERCENTAGES_PENDING: {
      return {
        ...state,
        femaleHeightCategoryPercentagesLoading: true
      };
    }

    case actionTypes.FIND_COMPARISON_EVENT_DATA_SET_FEMALE_HEIGHT_CATEGORY_PERCENTAGES_FULFILLED: {
      const comparisonDataSets = state.comparisonDataSets.slice();
      comparisonDataSets[comparisonDataSets.length - 1] = {
        ...state.comparisonDataSets[state.comparisonDataSets.length - 1],
        femaleHeightCategoryPercentages: action.payload.data.result
      };

      return {
        ...state,
        comparisonDataSets: comparisonDataSets,
        femaleHeightCategoryPercentagesLoading: false
      };
    }

    case actionTypes.ADD_DATA_SET_REPORTS_COMPARISON_DATA_SET: {
      return {
        ...state,
        comparisonDataSets: [...state.comparisonDataSets, action.payload]
      };
    }

    case actionTypes.REMOVE_DATA_SET_REPORTS_COMPARISON_DATA_SET: {
      const dataSet = action.payload;
      const index = state.comparisonDataSets
        .map(dataSet => dataSet.id)
        .indexOf(dataSet.id);

      const comparisonDataSets = state.comparisonDataSets.slice();
      if (index > -1) {
        comparisonDataSets.splice(index, 1);
      }

      return {
        ...state,
        comparisonDataSets: comparisonDataSets
      };
    }

    case actionTypes.UPDATE_EVENT_VALIDATION_STATUS_FULFILLED:
    case actionTypes.UPDATE_EVENT_ANNOTATED_PARTICIPANT_GENDER_FULFILLED:
    case actionTypes.TOGGLE_EVENT_SPECIAL_INTEREST_FULFILLED:
    case actionTypes.SAVE_EVENT_ANNOTATED_SUBJECT_AREA_CONTENT_FULFILLED:
    case actionTypes.DELETE_EVENT_ANNOTATED_SUBJECT_AREA_CONTENT_FULFILLED:
    case actionTypes.UPDATE_EVENT_ANNOTATED_PARTICIPANT_CLOTHING_TYPE_FULFILLED: {
      const event = action.payload.data.result;
      const index = state.events.items.map(e => e.id).indexOf(event.id);
      const events = state.events.items.slice();

      if (index > -1) {
        events[index] = mergeUpdatedWalk(
          events[index],
          event,
          action.type ===
            actionTypes.SAVE_EVENT_ANNOTATED_SUBJECT_AREA_CONTENT_FULFILLED ||
            action.type ===
              actionTypes.DELETE_EVENT_ANNOTATED_SUBJECT_AREA_CONTENT_FULFILLED
        );
      }

      return {
        ...state,
        events: events,
        events: {
          ...state.events,
          items: events
        }
      };
    }

    default: {
      return state;
    }
  }
};

export default eventDataSetDetailsReducer;
